import { useValidation } from "Hooks/useValidation";
import { useCallback, useEffect, useRef, useState } from "react";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import { CreditProvider } from "../ProviderSection/CreditProviderSection";
import MultiComboboxSetter from "Views/Components/Combobox/MultiComboboxSetter";
import { ComboboxOption } from "Views/Components/Combobox/Combobox";
import useGqlBusiness from "Hooks/Api/useGqlBusiness";
import ComboboxSetter from "Views/Components/Combobox/ComboboxSetter";
import axios from "axios";
import { SERVER_URL } from "Constants";
import alertToast from "Util/ToastifyUtils";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";

export type InterimFormRequestData = {
	businessEntityId: string;
	businessEntityName: string;
	companyName: string;
	companyNumber: string;
	companyAddress: string;
	zipCode: string;
	city: string;
	providerProducts: CreditReportProviderProductSummary[];
}

type CreditReportProviderProductSummary = {
	businessEntityCreditReportSettingsId: string;
	providerId: string;
	providerName: string;
	providerProductId: string;
	productName: string;
	productCode: string;
	productDescription: string;
	isEnabled: boolean;
	originalPrice: number;
	price: number;
	discount: number;
};

const getAvailableCreditProviderProducts = async (
	providerId: string,
	businessEntityId: string,
	countryCode: string
): Promise<ComboboxOption<CreditReportProviderProductSummary>[]> => {
	const params = {
		businessEntityId: businessEntityId,
		countryCode: countryCode,
	}
	try {
		const response = await axios.get<CreditReportProviderProductSummary[]>(`${SERVER_URL}/api/organisation-provider/${providerId}/products`, {
			params: params
		});
		const options = response.data.map(product => ({
			value: product,
			display: `${product.productName}`,
			disabled: !product.isEnabled
		}));

		options.sort((a, b) => a.display.localeCompare(b.display));

		return options;
	} catch (error) {
		console.error(error);
		alertToast("Failed to get credit report products. Please try again or contact support", "error");
		return [];
	}
}

type Props = {
	interimFormData: InterimFormRequestData;
	updateInterimFormData: (data: InterimFormRequestData) => void;
	organisationId: string;
	creditProvider: CreditProvider;
	countryCode: string;
};

export const InterimFormSection: React.FunctionComponent<Props> = ({
	interimFormData,
	organisationId,
	updateInterimFormData,
	creditProvider,
	countryCode
}) => {
	const availableBusinessEntitiesResponse = useGqlBusiness({filterByOrgId: organisationId});
	const [businessEntityId, setBusinessEntityId] = useState<string>();
	const [availableProducts, setAvailableProducts] = useState<
		ComboboxOption<CreditReportProviderProductSummary>[]
	>([]);

	const fetchProducts = useCallback(async (entityId: string) => {
		try {
			const products = await getAvailableCreditProviderProducts(
				creditProvider.id,
				entityId,
				countryCode
			);
			setAvailableProducts(products);
		} catch (error) {
			console.error('Error fetching products:', error);
			setAvailableProducts([]);
		}
	}, [creditProvider.id, organisationId, countryCode]);

	useEffect(() => {
		if (availableBusinessEntitiesResponse.type === "data" && availableBusinessEntitiesResponse.data) {
			if (availableBusinessEntitiesResponse.data.length === 1) {
				const entity = availableBusinessEntitiesResponse.data[0];
				if (entity.id !== interimFormData.businessEntityId) {
					updateInterimFormData({
						...interimFormData,
						businessEntityId: availableBusinessEntitiesResponse.data[0].id,
						businessEntityName: availableBusinessEntitiesResponse.data[0].name,
						providerProducts: []
					
					});
				}
				setBusinessEntityId(entity.id);
			}
		}
	}, [availableBusinessEntitiesResponse.data, availableBusinessEntitiesResponse.type, interimFormData, updateInterimFormData]);

	useEffect(() => {
		if (businessEntityId) {
			fetchProducts(businessEntityId);
		}
	}, [businessEntityId, fetchProducts]);


	const validation = useValidation({
		companyName: {
			value: interimFormData.companyName,
			isRequired: true,
			requiredText: "Company Name is required",
		},
		companyNumber: {
			value: interimFormData.companyNumber,
			isRequired: false,
			validate: value => value.assertRule(data => !!data && data.length <= 50, "Company Number must be 50 characters or less")
		},
		companyAddress: {
			value: interimFormData.companyAddress,
			isRequired: false
		},
		zipCode: {
			value: interimFormData.zipCode,
			isRequired: false
		},
		city: {
			value: interimFormData.city,
			isRequired: false
		}
	}, false);

	return (
		<>
			{availableBusinessEntitiesResponse.type === "loading" && (
				<InlineSpinner />
			)}
			{availableBusinessEntitiesResponse.data && availableBusinessEntitiesResponse.type === "data" && (
				<>
					<ComboboxSetter
						value={businessEntityId}
						setValue={newValue => {
							setBusinessEntityId(newValue);
							updateInterimFormData({
								...interimFormData,
								businessEntityId: newValue,
								businessEntityName: availableBusinessEntitiesResponse.data?.find(entity => entity.id === newValue)?.name || "",
								providerProducts: []
							});
						}}
						options={availableBusinessEntitiesResponse.data.map(entity => ({
							value: entity.id,
							display: entity.name
						}))}
						getOptionValue={data => data}
						label="Select Business Entity"
						placeholder="Choose business entity"
					/>
					{!!businessEntityId && (
						<>
							{availableProducts.length > 0 && (
								<>
									<TextFieldSetter
										value={interimFormData.companyName}
										setValue={newvalue => {
											updateInterimFormData({...interimFormData, companyName: newvalue})
										}}
										label="Company Name"
										placeholder="Company Name"
										isRequired
										errors={validation.get("companyName")}
									/>
									<TextFieldSetter
										value={interimFormData.companyNumber}
										setValue={newvalue => {
											updateInterimFormData({...interimFormData, companyNumber: newvalue})
										}}
										label="Company Number"
										placeholder="Company Number"
										errors={validation.get("companyNumber")}
									/>
									<TextFieldSetter
										value={interimFormData.companyAddress}
										setValue={newvalue => {
											updateInterimFormData({...interimFormData, companyAddress: newvalue})
										}}
										label="Company Address"
										placeholder="Company Address"
										errors={validation.get("companyAddress")}
									/>
									<TextFieldSetter
										value={interimFormData.zipCode}
										setValue={newvalue => {
											updateInterimFormData({...interimFormData, zipCode: newvalue})
										}}
										label="Zip Code"
										placeholder="Zip Code"
										errors={validation.get("zipCode")}
									/>
									<TextFieldSetter
										value={interimFormData.city}
										setValue={newvalue => {
											updateInterimFormData({...interimFormData, city: newvalue})
										}}
										label="City"
										placeholder="City"
										errors={validation.get("city")}
									/>
									<MultiComboboxSetter
										label="Credit report product"
										tooltip="Select the credit report product to generate"
										value={interimFormData.providerProducts}
										setValue={data => {
											updateInterimFormData({...interimFormData, providerProducts: data})
										}}
										options={availableProducts}
										searchable
										getOptionValue={value => value?.businessEntityCreditReportSettingsId}
									/>
								</>
							)}
							{availableProducts.length === 0 && (
								<p className="alert alert__warning">No credit report products available for the selected business entity.</p>
							)}
						</>
					)}
				</>
			)}
		</>
	)
}